/**
 * Search Slice
 *
 * Zustand slice for search state management.
 * Handles search queries, results, loading states, and quota tracking.
 *
 * @validates Requirements 8.7
 */

import type { StateCreator } from 'zustand';
import type { StoreState, SearchSlice, SearchState } from './types';

/**
 * Initial search state
 */
const initialSearchState: SearchState = {
  query: '',
  results: [],
  totalCount: 0,
  isLoading: false,
  searchTimeMs: null,
  cached: false,
  quota: null,
  error: null,
};

/**
 * Create the search slice for the Zustand store
 */
export const createSearchSlice: StateCreator<StoreState, [], [], SearchSlice> = (
  set,
  get
) => ({
  // Initial state
  ...initialSearchState,

  // Actions
  setQuery: (query) => set({ query }),

  setResults: (words, totalCount, searchTimeMs, cached) =>
    set({
      results: words,
      totalCount,
      searchTimeMs,
      cached,
      isLoading: false,
      error: null,
    }),

  clearResults: () =>
    set({
      results: [],
      totalCount: 0,
      searchTimeMs: null,
      cached: false,
      error: null,
    }),

  setSearchLoading: (isLoading) => set({ isLoading }),

  setQuota: (quota) => set({ quota }),

  decrementQuota: () => {
    const { quota } = get();
    if (quota && quota.remaining > 0 && !quota.isPremium) {
      set({
        quota: {
          ...quota,
          remaining: quota.remaining - 1,
        },
      });
    }
  },

  setSearchError: (error) => set({ error, isLoading: false }),

  clearSearchError: () => set({ error: null }),

  resetSearch: () => set(initialSearchState),
});
